import { parseDateString } from '../../utils/dateUtils'
import TimelineRow from './TimelineRow'

/**
 * TimelineDateGroup - Groups timeline rows under a sticky date header
 *
 * Provides visual grouping for multiple timeline items that share the same date.
 * Uses sticky positioning for the date header to maintain context during scroll.
 *
 * @param {Object} props
 * @param {string} props.date - ISO date string (YYYY-MM-DD)
 * @param {Array} props.items - Array of timeline items to display under this date
 * @param {Function} props.onConfirm - Confirm handler passed to TimelineRow: (item, editedQuantities) => void
 * @param {Function} props.onDismiss - Dismiss handler passed to TimelineRow: (item) => void
 * @param {Function} props.onQuantityChange - Quantity change handler passed to TimelineRow: (itemId, productId, quantity) => void
 * @param {Function} props.onStatusChange - Status change handler passed to TimelineRow: (itemId, newStatus) => void
 * @param {Function} props.onEdit - Edit handler passed to TimelineRow: (item) => void
 * @param {Function} props.onEditRecurringOrder - Edit recurring order handler passed to TimelineRow: (item) => void
 * @param {Function} props.onViewInvoice - View invoice handler passed to TimelineRow: (item) => void
 * @param {Array} props.confirmingIds - Array of item IDs currently being confirmed
 * @param {string} props.className - Additional CSS classes
 */
export default function TimelineDateGroup({
  date,
  items = [],
  onConfirm,
  onDismiss,
  onQuantityChange,
  onStatusChange,
  onEdit,
  onEditRecurringOrder,
  onViewInvoice,
  onCustomerClick,
  onAdjustmentChange,
  onAcceptAdjustments,
  productTypes,
  confirmingIds = [],
  className
}) {
  // Format date as "Sun 22 December 2025"
  const formatDateHeader = (dateString) => {
    const dateObj = parseDateString(dateString)
    const dayName = dateObj.toLocaleDateString('en-GB', { weekday: 'short' })
    const datePart = dateObj.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
    return `${dayName} ${datePart}`
  }

  // Compute daily totals
  const totalRevenue = items.reduce((sum, item) => sum + (item.total || 0), 0)
  const totalKg = items.reduce((sum, item) => {
    const itemKg = (item.items || []).reduce((s, li) => s + (li.quantity || 0), 0)
    return sum + itemKg
  }, 0)

  if (!items || items.length === 0) {
    return null
  }

  return (
    <div className={className}>
      {/* Sticky date header - matches pattern from SalesHistorySection */}
      <div className="bg-white border-b px-6 py-1.5 flex justify-between items-center">
        <span className="text-xs font-semibold text-muted-foreground">
          {formatDateHeader(date)}
        </span>
        <span className="text-xs font-semibold text-muted-foreground">
          £{totalRevenue.toFixed(2)}  {totalKg.toFixed(1)}kg
        </span>
      </div>

      {/* Timeline rows for this date */}
      {items.map((item) => (
        <TimelineRow
          key={item.id}
          item={item}
          onConfirm={onConfirm}
          onDismiss={onDismiss}
          onQuantityChange={onQuantityChange}
          onStatusChange={onStatusChange}
          onEdit={onEdit}
          onEditRecurringOrder={onEditRecurringOrder}
          onViewInvoice={onViewInvoice}
          onCustomerClick={onCustomerClick}
          onAdjustmentChange={onAdjustmentChange}
          onAcceptAdjustments={onAcceptAdjustments}
          productTypes={productTypes}
          isConfirming={confirmingIds.includes(item.id)}
        />
      ))}
    </div>
  )
}
